import { Link } from "react-router-dom";
import Loader from "@/components/Loader/Loader";
import { combineClasses } from "@/lib/utils";
import Sidebar from "@/layouts/PostLogin/components/Sidebar/Sidebar";
import {
  ROLE_CONTENT,
  getJourneyStepIndex,
  type AuthRole,
  type JourneyStepKey,
} from "@/pages/Auth/authConfig";
import styles from "./JourneyFrame.module.scss";

interface JourneyFrameProps {
  role: AuthRole;
  activeStep: JourneyStepKey;
  eyebrow?: string | undefined;
  title: React.ReactNode;
  description?: React.ReactNode | undefined;
  children: React.ReactNode;
  actionLabel: string;
  actionTo?: string | undefined;
  actionType?: "link" | "button" | undefined;
  actionButtonType?: "button" | "submit" | undefined;
  actionDisabled?: boolean | undefined;
  onAction?: (() => void) | undefined;
  onBack?: (() => void) | undefined;
  backLabel?: string | undefined;
  bodyClassName?: string | undefined;
  contentClassName?: string | undefined;
  alignBodyCenter?: boolean | undefined;
  footerNote?: React.ReactNode | undefined;
  progressPercent?: number | undefined;
  hideProgressLabel?: boolean | undefined;
  isContentLoading?: boolean | undefined;
}

const JourneyFrame = ({
  role,
  activeStep,
  eyebrow,
  title,
  description,
  children,
  actionLabel,
  actionTo,
  actionType = "link",
  actionButtonType = "button",
  actionDisabled = false,
  onAction,
  onBack,
  backLabel = "Back",
  bodyClassName,
  contentClassName,
  alignBodyCenter = false,
  footerNote,
  progressPercent,
  hideProgressLabel = false,
  isContentLoading = false,
}: JourneyFrameProps) => {
  const steps = ROLE_CONTENT[role].signUpSteps;
  const currentStep = getJourneyStepIndex(steps, activeStep) + 1;
  const totalSteps = steps.length;
  const resolvedProgressPercent =
    progressPercent ?? (currentStep / totalSteps) * 100;

  const actionNode =
    actionType === "button" ? (
      <button
        className={styles.primaryButton}
        disabled={actionDisabled}
        onClick={onAction}
        type={actionButtonType}
      >
        {actionLabel}
      </button>
    ) : (
      <Link className={styles.primaryButton} to={actionTo || "#"}>
        {actionLabel}
      </Link>
    );

  return (
    <section className={styles.page}>
      <div className={styles.card}>
        <Sidebar activeStep={activeStep} role={role} />

        <div className={combineClasses(styles.contentPanel, contentClassName)}>
          {isContentLoading ? (
            <div className={styles.contentLoader}>
              <Loader fullscreen={false} size={92} />
            </div>
          ) : null}

          <div className={styles.inner}>
            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${resolvedProgressPercent}%` }}
                />
              </div>
              {!hideProgressLabel ? (
                <span className={styles.progressText}>
                  Step {currentStep} of {totalSteps}
                </span>
              ) : null}
            </div>

            <div className={styles.headingBlock}>
              {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
              {title ? <h1 className={styles.title}>{title}</h1> : null}
              {description ? (
                <p className={styles.description}>{description}</p>
              ) : null}
            </div>

            <div
              className={combineClasses(
                styles.body,
                alignBodyCenter && styles.bodyCentered,
                bodyClassName,
              )}
            >
              {children}
            </div>

            <div className={styles.footerRow}>
              <p className={styles.footerText}>
                {footerNote || null}
              </p>
              <div className={styles.buttonGroup}>
                {onBack ? (
                  <button
                    className={styles.secondaryButton}
                    onClick={onBack}
                    type="button"
                  >
                    {backLabel}
                  </button>
                ) : null}
                {actionNode}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JourneyFrame;
